import React, { useState, useEffect } from 'react';
import {
  CalendarLtr24Regular,
  CalendarLtr24Filled,
  Home24Regular,
  Home24Filled,
  DocumentBulletList24Regular,
  DocumentBulletList24Filled,
  Chat24Regular,
  Chat24Filled,
  CheckmarkSquare24Regular,
  CheckmarkSquare24Filled,
  PersonSwap24Regular,
  PersonSwap24Filled,
  DataUsageSparkle20Filled,
  DataUsageSparkle20Regular,
} from '@fluentui/react-icons';

import { Box } from '../../theme/fluent';
import { PAGE_PATHS, COMPONENT_IDS } from '../../features/app-config/constants/appConstants';
import { WelcomeLoadingScreen, RulesOfTheRoadDialog } from '../common';
import { useDashboardPreparation } from '../common/hooks/useDashboardPreparation';
import { useAppLayoutStyles } from './AppLayout.styles';
import { AppLayoutProps, INavigationItem } from './AppLayout.types';
import { MainContent, Sidebar } from './app-layout-components';

export const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const { classes, appRootStyle } = useAppLayoutStyles();
  const { isLoading, loadingText, availableMenuItems, canUsePanelComponent } = useDashboardPreparation();

  // Rules of the Road dialog state - show on every fresh load
  const [showRulesDialog, setShowRulesDialog] = useState(false);

  // Show dialog after loading screen completes
  useEffect(() => {
    if (!isLoading) setShowRulesDialog(true);
  }, [isLoading]);

  const handleAcceptRules = () => {
    (document.activeElement as HTMLElement)?.blur();
    setShowRulesDialog(false);
    // optional chain the call itself too: jsdom (unit tests) doesn't implement scrollTo on elements
    document.getElementById('main-scrollable-content')?.scrollTo?.({ top: 0 });
  };

  // Tabster accessibility issue fix
  useEffect(() => {
    const fixDummyInputs = () => {
      document.querySelectorAll('[data-tabster-dummy]').forEach((el) => {
        if (el.closest('[aria-hidden="true"]')) el.setAttribute('tabindex', '-1');
      });
    };
    fixDummyInputs();
    const observer = new MutationObserver(fixDummyInputs);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-hidden', 'data-tabster-dummy'],
    });
    return () => observer.disconnect();
  }, []);

  if (isLoading) {
    return <WelcomeLoadingScreen loadingText={loadingText} decorativeImage="/images/loading-sphere.png" />;
  }

  const iconMap: Record<string, React.ReactElement> = {
    [PAGE_PATHS.DASHBOARD]: <Home24Regular />,
    [PAGE_PATHS.ROBIN_CHAT]: <Chat24Regular />,
    [PAGE_PATHS.INSIGHTS_HUB]: <DataUsageSparkle20Regular />,
  };

  const activeIconMap: Record<string, React.ReactElement> = {
    [PAGE_PATHS.DASHBOARD]: <Home24Filled />,
    [PAGE_PATHS.ROBIN_CHAT]: <Chat24Filled />,
    [PAGE_PATHS.INSIGHTS_HUB]: <DataUsageSparkle20Filled />,
  };

  // Get navigation items based on user role
  const navigationItems = availableMenuItems.map((page) => ({
    text: page.name,
    icon: iconMap[page.path] || <Home24Regular />,
    activeIcon: activeIconMap[page.path] || <Home24Filled />,
    path: page.path,
    description: page.description,
  }));

  // Dashboard item (always first, separate from tools)
  const dashboardItem = navigationItems.find((item) => item.path === PAGE_PATHS.DASHBOARD);

  // Custom shortcuts for panels (only shown if user has permission)
  const customShortcuts: INavigationItem[] = [
    {
      text: 'Scheduling and meetings',
      icon: <CalendarLtr24Regular />,
      activeIcon: <CalendarLtr24Filled />,
      path: PAGE_PATHS.DASHBOARD,
      panelId: COMPONENT_IDS.DIARY_MANAGEMENT_PANEL,
      activeOn: [COMPONENT_IDS.DIARY_MANAGEMENT_PANEL, COMPONENT_IDS.MEETING_AVAILABILITY_PANEL],
      description: 'Manage your calendar and meetings',
      canUse: canUsePanelComponent(COMPONENT_IDS.DIARY_MANAGEMENT_PANEL),
    },
    {
      text: 'Thread Summarisation',
      icon: <DocumentBulletList24Regular />,
      activeIcon: <DocumentBulletList24Filled />,
      path: PAGE_PATHS.DASHBOARD,
      panelId: COMPONENT_IDS.THREAD_SUMMARISATION_PANEL,
      description: 'Summarise email threads',
      canUse: canUsePanelComponent(COMPONENT_IDS.THREAD_SUMMARISATION_PANEL),
    },
    {
      text: 'To-do',
      icon: <CheckmarkSquare24Regular />,
      activeIcon: <CheckmarkSquare24Filled />,
      path: PAGE_PATHS.DASHBOARD,
      panelId: COMPONENT_IDS.ACTION_ITEMS_PANEL,
      description: 'Manage your tasks',
      canUse: canUsePanelComponent(COMPONENT_IDS.ACTION_ITEMS_PANEL),
    },
    {
      text: 'Handover Support',
      icon: <PersonSwap24Regular />,
      activeIcon: <PersonSwap24Filled />,
      path: PAGE_PATHS.DASHBOARD,
      panelId: COMPONENT_IDS.HANDOVER_SUPPORT_PANEL,
      description: 'Handover preparation and documentation',
      canUse: canUsePanelComponent(COMPONENT_IDS.HANDOVER_SUPPORT_PANEL),
    },
    {
      text: 'Insights Hub',
      icon: <DataUsageSparkle20Regular />,
      activeIcon: <DataUsageSparkle20Filled />,
      path: PAGE_PATHS.DASHBOARD,
      panelId: COMPONENT_IDS.INSIGHTS_HUB_PANEL,
      description: 'Insights and analytics hub',
      canUse: canUsePanelComponent(COMPONENT_IDS.INSIGHTS_HUB_PANEL),
    },
  ].filter((shortcut) => shortcut.canUse);

  return (
    <Box className={classes.appRoot} style={appRootStyle}>
      {/* Lets keyboard users bypass the nav and jump straight to the current page's content */}
      <a id="skip-to-main-content" href="#main-scrollable-content" className={classes.skipLink}>
        Skip to main content
      </a>
      <Box className={classes.appRootInner}>
        <Sidebar
          dashboardItem={dashboardItem}
          customShortcuts={customShortcuts}
          otherNavigationItems={navigationItems.filter((item) => item.path !== PAGE_PATHS.DASHBOARD)}
        />
        <MainContent>{children}</MainContent>
      </Box>

      {/* Rules of the Road Dialog */}
      {showRulesDialog && <RulesOfTheRoadDialog open={showRulesDialog} onAccept={handleAcceptRules} />}
    </Box>
  );
};
